import { SO_MA_DU_PHONG } from '@qlk/shared';
import { Check, Copy, Download, ShieldCheck, Smartphone } from 'lucide-react';
import { useState } from 'react';

import { Nut, O } from '../../../ui/index.js';
import { useBatDau2fa, useXacNhan2fa } from '../api.js';

/**
 * Luồng **bật xác thực hai bước**, ba bước trên một màn hình.
 *
 * Dùng chung cho hai nơi: màn **ép bật** (`TrangBat2fa`, khi `buocBat2fa` của
 * `/core/me` bật) và khối 2FA ở trang "Tài khoản của tôi". Một bản, vì đây là
 * luồng dễ làm người dùng kẹt nhất trong cả app — hai bản là hai cách kẹt.
 *
 * Bước 3 **chặn đường ra**: mười mã dự phòng hiện đúng một lần, và nút đi tiếp
 * chỉ mở sau khi người dùng tự khẳng định đã lưu. Không có cái chặn đó thì
 * người đóng tab ở bước này mất hẳn đường khôi phục khi mất điện thoại, và
 * đường duy nhất còn lại là nhờ một quản trị khác gỡ hộ.
 */

const TEN_FILE_MA = 'wm-media-ma-du-phong-2fa.txt';

export interface Bat2faProps {
  /** Gọi khi người dùng đã xác nhận lưu mã xong. */
  onXong?: () => void;
  /** Nhãn của nút cuối. Màn ép bật dùng "Vào app". */
  nhanXong?: string;
}

export function Bat2fa({ onXong, nhanXong = 'Xong' }: Bat2faProps) {
  const batDau = useBatDau2fa();
  const xacNhan = useXacNhan2fa();

  const [ma, setMa] = useState('');
  const [daLuu, setDaLuu] = useState(false);

  const maDuPhong = xacNhan.data?.maDuPhong;

  /* Bước 3: đã có mã dự phòng ⇒ chỉ còn việc lưu chúng. */
  if (maDuPhong) {
    return (
      <KhoiMaDuPhong
        maDuPhong={maDuPhong}
        daLuu={daLuu}
        onDaLuu={() => {
          setDaLuu(true);
        }}
        onXong={onXong}
        nhanXong={nhanXong}
      />
    );
  }

  /* Bước 1: chưa sinh secret. */
  if (!batDau.data) {
    return (
      <div>
        <HuongDanCaiApp />
        {batDau.isError && (
          <p role="alert" className="mt-4 text-phu text-bad-chu">
            {batDau.error.message}
          </p>
        )}
        <Nut
          kieu="chinh"
          className="mt-5"
          dangChay={batDau.isPending}
          onClick={() => {
            batDau.mutate();
          }}
        >
          Bắt đầu — hiện mã QR
        </Nut>
      </div>
    );
  }

  /* Bước 2: quét QR rồi nhập mã. */
  const { qrSvg, secretDeDoc, phieu } = batDau.data;

  return (
    <div>
      <HuongDanCaiApp />

      <div className="mt-5 grid grid-cols-1 gap-6 @min-[640px]/noi-dung:grid-cols-[auto_1fr]">
        {/*
          SVG do máy chủ sinh, nhúng thẳng bằng `dangerouslySetInnerHTML`.

          An toàn vì nguồn là `qrcode` chạy ở máy chủ trên một URI mà chính máy
          chủ vừa dựng (`otpauth://…` với secret base32) — không có một byte nào
          của người dùng trong đó. Có bài test đòi SVG không chứa `<script`,
          `href`, `url(` hay `onload`.
        */}
        <div
          data-qr-2fa
          aria-label="Mã QR để quét bằng app xác thực"
          role="img"
          className="w-fit rounded-the border border-vien bg-white p-3"
          dangerouslySetInnerHTML={{ __html: qrSvg }}
        />

        <div>
          <p className="text-co-ban text-chu-phu">
            Quét mã QR bằng app xác thực. Nếu điện thoại không quét được, mở app rồi chọn{' '}
            <strong className="font-medium text-chu">Nhập khoá thủ công</strong> và gõ dãy dưới đây:
          </p>
          <p
            data-secret-2fa
            className="mt-2 rounded-[10px] bg-nen px-3 py-2 font-mono text-co-ban tracking-wider text-chu select-all"
          >
            {secretDeDoc}
          </p>

          <div className="mt-4 max-w-[260px]">
            <O
              label="Mã 6 số trong app"
              value={ma}
              onChange={setMa}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={7}
              required
              moTa="Mã đổi mỗi 30 giây. Nhập mã đang hiện."
            />
          </div>

          {xacNhan.isError && (
            <p role="alert" className="mt-3 text-phu text-bad-chu">
              {xacNhan.error.message}
            </p>
          )}

          <div className="mt-4 flex flex-wrap gap-3">
            <Nut
              kieu="chinh"
              disabled={ma.replace(/\s/g, '').length !== 6}
              dangChay={xacNhan.isPending}
              onClick={() => {
                xacNhan.mutate({ phieu, ma });
              }}
            >
              Bật xác thực hai bước
            </Nut>
            <Nut
              dangChay={batDau.isPending}
              onClick={() => {
                setMa('');
                batDau.mutate();
              }}
            >
              Làm mới mã QR
            </Nut>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function HuongDanCaiApp() {
  return (
    <div>
      <h3 className="flex items-center gap-2 text-co-ban font-semibold text-chu">
        <Smartphone size={18} aria-hidden="true" />
        Cần một app xác thực trên điện thoại
      </h3>
      <ol className="mt-2 list-decimal space-y-1 pl-5 text-co-ban text-chu-phu">
        <li>
          Cài <strong className="font-medium text-chu">Google Authenticator</strong> hoặc{' '}
          <strong className="font-medium text-chu">Microsoft Authenticator</strong> từ App Store /
          Google Play. App nào theo chuẩn TOTP cũng được.
        </li>
        <li>Mở app, chọn thêm tài khoản rồi quét mã QR bên dưới.</li>
        <li>Nhập mã 6 số mà app hiện ra để xác nhận.</li>
        <li>Lưu lại {SO_MA_DU_PHONG} mã dự phòng — đó là đường vào khi bạn mất điện thoại.</li>
      </ol>
    </div>
  );
}

/* ------------------------------------------------------------------ */

interface KhoiMaDuPhongProps {
  maDuPhong: string[];
  daLuu: boolean;
  onDaLuu: () => void;
  onXong?: (() => void) | undefined;
  nhanXong: string;
}

function KhoiMaDuPhong({ maDuPhong, daLuu, onDaLuu, onXong, nhanXong }: KhoiMaDuPhongProps) {
  const [daChep, setDaChep] = useState(false);

  const chu = maDuPhong.join('\n');

  async function chep() {
    try {
      await navigator.clipboard.writeText(chu);
      setDaChep(true);
    } catch {
      /* Trình duyệt không cho ghi clipboard (không có HTTPS, hoặc người dùng
         chặn). Mã vẫn hiện trên màn hình và vẫn tải được, nên chỉ cần không
         làm gì — đừng bật cờ "đã chép" cho một việc chưa xảy ra. */
      setDaChep(false);
    }
  }

  function tai() {
    /*
     * Tạo file ngay trong trình duyệt bằng `Blob` + `URL.createObjectURL`:
     * không gửi mã qua một lượt request nào nữa, nên chúng không nằm trong log
     * của máy chủ hay của proxy.
     */
    const url = URL.createObjectURL(new Blob([`${chu}\n`], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = TEN_FILE_MA;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div data-khoi="ma-du-phong">
      <h3 className="flex items-center gap-2 text-co-ban font-semibold text-ok-chu">
        <ShieldCheck size={18} aria-hidden="true" />
        Đã bật. Giờ lưu {String(maDuPhong.length)} mã dự phòng này.
      </h3>
      <p className="mt-2 text-co-ban text-chu-phu">
        Mỗi mã dùng được <strong className="font-medium text-chu">một lần</strong>, thay cho mã 6 số
        khi bạn mất điện thoại. Đây là lần duy nhất chúng hiện ra — máy chủ chỉ giữ bản băm. Lưu vào
        trình quản lý mật khẩu, hoặc in ra giấy.
      </p>

      <ul className="mt-4 grid grid-cols-2 gap-2 rounded-the border border-vien bg-nen p-4 @min-[640px]/noi-dung:grid-cols-5">
        {maDuPhong.map((ma) => (
          <li key={ma} className="font-mono text-co-ban tracking-wider text-chu select-all">
            {ma}
          </li>
        ))}
      </ul>

      <div className="mt-4 flex flex-wrap gap-3">
        <Nut
          onClick={() => {
            void chep();
          }}
        >
          {daChep ? <Check size={18} aria-hidden="true" /> : <Copy size={18} aria-hidden="true" />}
          {daChep ? 'Đã chép' : 'Chép tất cả'}
        </Nut>
        <Nut onClick={tai}>
          <Download size={18} aria-hidden="true" />
          Tải file .txt
        </Nut>
      </div>

      <label className="mt-5 flex items-start gap-3 text-co-ban text-chu">
        <input
          type="checkbox"
          checked={daLuu}
          onChange={onDaLuu}
          className="mt-0.5 size-5 shrink-0 rounded-[4px] border border-vien accent-nhan"
        />
        <span>Tôi đã lưu {String(maDuPhong.length)} mã dự phòng ở nơi an toàn.</span>
      </label>

      {onXong && (
        <div className="mt-5">
          {/*
            Nút ra khỏi màn này **khoá** tới khi ô trên được tích. Đóng tab ở
            bước này mà chưa lưu mã là mất hẳn đường khôi phục khi mất điện
            thoại; đường duy nhất còn lại là nhờ một quản trị khác gỡ hộ.
          */}
          <Nut kieu="chinh" disabled={!daLuu} onClick={onXong}>
            {nhanXong}
          </Nut>
          {!daLuu && (
            <p className="mt-2 text-phu text-chu-mo">
              Tích vào ô trên để đi tiếp. Mã sẽ không hiện lại lần nữa.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
